// ════════════════════════════════════════════════════════════════════
// src/storeVisits/form-spec.js
//
// Detailed SVR form structure — mechanical translation of
// SVR-DETAILED-FORM-SPEC.md. Edit the markdown spec first, then
// reflect changes here. Keep in sync.
//
// ⚠️ KEYS ARE STABLE — renaming a section key or question key
// orphans historical visit_data JSONB in production. Add new keys,
// don't rename existing ones.
//
// Question types:
//   'rag'         — Green / Amber / Red selector
//   'comment'     — free-text textarea (always optional)
//   'photos'      — photo upload, max 5 per section
//   'shift-embed' — read-only live data widget + RAG rating
// ════════════════════════════════════════════════════════════════════

export const SECTIONS = [
  {
    key: 'shiftCompliance',
    title: 'Shift Compliance',
    description: 'Pre-visit check — review live data, then rate what you see vs what you expect.',
    questions: [
      { key: 'rota',         type: 'shift-embed', embedKey: 'rota',         label: 'Current week rota for this store' },
      { key: 'checklists',   type: 'shift-embed', embedKey: 'checklists',   label: 'Opening / closing / weekly checklist completion this week' },
      { key: 'clockIns',     type: 'shift-embed', embedKey: 'clockIns',     label: 'Today\'s clock-in status (who\'s in, lates, no-shows)' },
      { key: 'toilBalances', type: 'shift-embed', embedKey: 'toilBalances', label: 'Staff TOIL balances at this store' },
      { key: 'comments',     type: 'comment',                                label: 'Any comments on Shift compliance?' },
    ],
  },
  {
    key: 'shopFront',
    title: 'Shop Front / Windows',
    questions: [
      { key: 'clean',        type: 'rag',     label: 'Are the shop front and windows clean?' },
      { key: 'priced',       type: 'rag',     label: 'Are the shop front and windows priced?' },
      { key: 'posters',      type: 'rag',     label: 'Are the correct posters and products displayed at the front?' },
      { key: 'matchPosters', type: 'rag',     label: 'Do the window products match the posters?' },
      { key: 'photos',       type: 'photos',  label: 'Upload supporting pictures' },
      { key: 'comments',     type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'generalAppearance',
    title: 'General Store Appearance',
    questions: [
      { key: 'easyWalkAround', type: 'rag',     label: 'Is the store easy to walk around?' },
      { key: 'tillArea',       type: 'rag',     label: 'Is the till area clean and tidy?' },
      { key: 'shopFloor',      type: 'rag',     label: 'Is the shop floor clean?' },
      { key: 'music',          type: 'rag',     label: 'Is the correct music playing at good volume?' },
      { key: 'videos',         type: 'rag',     label: 'Are the correct videos playing on TV screens?' },
      { key: 'tabletsCharged', type: 'rag',     label: 'Are tablets charged and working?' },
      { key: 'phonesLocked',   type: 'rag',     label: 'Are mobile phones in lockers and not in pockets?' },
      { key: 'stockRoom',      type: 'rag',     label: 'Is the stock room clean and tidy?' },
      { key: 'onePager',       type: 'rag',     label: 'Is the Store One Pager being used?' },
      { key: 'noticeBoard',    type: 'rag',     label: 'Is the notice board up to date?' },
      { key: 'dailyUpdates',   type: 'rag',     label: 'Are all staff told about daily updates?' },
      { key: 'uniform',        type: 'rag',     label: 'Are all staff wearing correct uniform?' },
      { key: 'emails',         type: 'rag',     label: 'Have all emails been read within 6 hours and EOD emails sent correctly?' },
      { key: 'photos',         type: 'photos',  label: 'Upload supporting pictures' },
      { key: 'comments',       type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'pricing',
    title: 'Pricing',
    questions: [
      { key: 'allPriced',         type: 'rag',     label: 'Do all products have prices?' },
      { key: 'priceChangesDone',  type: 'rag',     label: 'Have all new price changes been done in store?' },
      { key: 'promoPrices',       type: 'rag',     label: 'Are promotion prices correct?' },
      { key: 'dailyPriceCheck',   type: 'rag',     label: 'Is the daily price check completed?' },
      { key: 'photos',            type: 'photos',  label: 'Upload supporting pictures' },
      { key: 'comments',          type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'promotions',
    title: 'Promotions',
    questions: [
      { key: 'signs',       type: 'rag',     label: 'Are all promotion signs displayed correctly?' },
      { key: 'inStock',     type: 'rag',     label: 'Are all promotion products in stock?' },
      { key: 'displays',    type: 'rag',     label: 'Are promotion displays set up correctly?' },
      { key: 'pricesShown', type: 'rag',     label: 'Are promotion prices clearly shown?' },
      { key: 'photos',      type: 'photos',  label: 'Upload supporting pictures' },
      { key: 'comments',    type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'visualMerchandising',
    title: 'Visual Merchandising / Layout',
    questions: [
      { key: 'bulkSafe',         type: 'rag',     label: 'Are bulk displays neat and safe (max 2 products per stack)?' },
      { key: 'displayNeat',      type: 'rag',     label: 'Is the store display neat and tidy?' },
      { key: 'correctAreas',     type: 'rag',     label: 'Are products in the correct areas?' },
      { key: 'brandsTogether',   type: 'rag',     label: 'Are same brands together?' },
      { key: 'productsVisible',  type: 'rag',     label: 'Can customers see all products for sale?' },
      { key: 'shopFlow',         type: 'rag',     label: 'Is the shop flow correct (Baby to Preschool etc.)?' },
      { key: 'topSellers',       type: 'rag',     label: 'Are top selling products in key areas?' },
      { key: 'photos',           type: 'photos',  label: 'Upload supporting pictures' },
      { key: 'comments',         type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'demo',
    title: 'Demo',
    questions: [
      { key: 'correctItems',      type: 'rag',     label: 'Are only correct demo items being used?' },
      { key: 'centrallyApproved', type: 'rag',     label: 'Are only centrally approved demo products available (unmanned demo)?' },
      { key: 'weeklyDemoLine',    type: 'rag',     label: 'Is the weekly demo line being shown to customers?' },
      { key: 'meetingTargets',    type: 'rag',     label: 'Is the store meeting demo targets?' },
      { key: 'tillItems',         type: 'rag',     label: 'Are the correct items displayed by the till?' },
      { key: 'greetingCustomers', type: 'rag',     label: 'Are staff greeting every customer?' },
      { key: 'addOnTargets',      type: 'rag',     label: 'Do staff know their add-on targets and offer extras at till?' },
      { key: 'focusProducts',     type: 'rag',     label: 'Are focus products displayed in key areas?' },
      { key: 'top50',             type: 'rag',     label: 'Are Top 50 items in key store areas?' },
      { key: 'photos',            type: 'photos',  label: 'Upload supporting pictures' },
      { key: 'comments',          type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'stockDelivery',
    title: 'Stock & Delivery',
    questions: [
      { key: 'deliveriesProcessed', type: 'rag',     label: 'Are all deliveries processed?' },
      { key: 'returnsProcessed',    type: 'rag',     label: 'Are all returns processed?' },
      { key: 'negativeStocks',      type: 'rag',     label: 'Are there any negative stocks showing?' },
      { key: 'demoDamagedRemoved',  type: 'rag',     label: 'Have demo and damaged items been removed from system?' },
      { key: 'photos',              type: 'photos',  label: 'Upload supporting pictures' },
      { key: 'comments',            type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'cash',
    title: 'Cash',
    questions: [
      { key: 'vaultCheck',         type: 'rag',     label: 'Has the vault/safe check been done and recorded?' },
      { key: 'cashCheck',          type: 'rag',     label: 'Has the cash check been done?' },
      { key: 'cashDiscrepancies',  type: 'rag',     label: 'Are there any cash over or short amounts?' },
      { key: 'cardPayments',       type: 'rag',     label: 'Are card payments recorded correctly?' },
      { key: 'photos',             type: 'photos',  label: 'Upload supporting pictures' },
      { key: 'comments',           type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'mysteryShopper',
    title: 'Mystery Shopper',
    questions: [
      { key: 'lastScore',  type: 'rag',     label: 'Was the last mystery shop score over 80% (Green), 70% (Amber), or below 70% (Red)?' },
      { key: 'actionPlan', type: 'rag',     label: 'Is there an action plan for mystery shop improvements?' },
      { key: 'comments',   type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'tablets',
    title: 'Tablets',
    questions: [
      { key: 'staffDemo', type: 'rag',     label: 'Can staff show how to complete a sale on the tablet?' },
      { key: 'comments',  type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'training',
    title: 'Training',
    questions: [
      { key: 'sixMonthTraining',  type: 'rag',     label: 'Is everyone up to date with their 6-month training?' },
      { key: 'failedActionPlan',  type: 'rag',     label: 'Do staff who failed training have an action plan?' },
      { key: 'enterTrainer',      type: 'rag',     label: 'Are staff using The EnterTrainer learning system?' },
      { key: 'comments',          type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'deliveryApps',
    title: 'Delivery Apps',
    questions: [
      { key: 'appsWorking', type: 'rag',     label: 'Are all delivery apps working and meeting targets?' },
      { key: 'comments',    type: 'comment', label: 'Any comments?' },
    ],
  },
  {
    key: 'maintenance',
    title: 'Maintenance / Safety',
    questions: [
      { key: 'lights',        type: 'rag',     label: 'Are all lights working?' },
      { key: 'tvScreens',     type: 'rag',     label: 'Are TV screens working?' },
      { key: 'mallRules',     type: 'rag',     label: 'Does the store meet mall safety rules?' },
      { key: 'fixturesSafe',  type: 'rag',     label: 'Are all fixtures safe and not damaged?' },
      { key: 'otherIssues',   type: 'comment', label: 'Any other maintenance issues?' },
      { key: 'photos',        type: 'photos',  label: 'Upload supporting pictures' },
      { key: 'comments',      type: 'comment', label: 'Any other comments?' },
    ],
  },
];

// Final section — lives on the parent store_visits row, not in section_data
export const FINAL_FIELDS = {
  overall_rag: {
    type: 'rag',
    label: 'What was the overall RAG rating?',
    required: true,
  },
  action_plan: {
    type: 'comment',
    label: 'Enter the store action plan below',
  },
  photos: {
    type: 'photos',
    sectionKey: 'overall',  // used as section_key in store_visit_photos
    label: 'Upload pictures of the store',
  },
};

// Metadata fields — lives on the parent store_visits row, collected before sections start
export const METADATA_FIELDS = {
  store:          { type: 'store-dropdown', label: 'Store',         required: true },
  duty_manager:   { type: 'text',           label: 'Duty Manager' },
  entry_time:     { type: 'datetime',       label: 'Entry time',    required: true },
  exit_time:      { type: 'datetime',       label: 'Exit time' },
  visit_type:     { type: 'visit-type',     label: 'Visit type',    required: true },
};
